import { Controller } from '@hotwired/stimulus';
import Axios from '../axios';
import SliderController from './slider-controller';

export default class extends Controller {
    static targets = ['container'];
    static values = {
        eventDateParam: { type: String, default: 'event_date' },
    };

    // Class name constants
    static classes = {
        currentClass: '-current',
        noEventsClass: '-no-events',
        loadingClass: 'loading',
        noEventsStateClass: 'no-events',
    };

    // Selector constants
    static selectors = {
        calendarCell: '.calendar-cell',
        calendarStripItem: '.calendar-strip__item',
        calendarStripItemTitle: '.calendar-strip__item__title',
        splideList: '.splide__list',
        allEventsLink: '.calendar-all-events-link',
        resetButtons: '.slider-buttons .btn-back',
        linkBelow: '.slider-buttons .btn-primary',
    };

    connect() {
        this.filters = {};
        this.currentLanguageCode = document.documentElement.lang.split('-')[0];
        this.postType = 'event'; // default

        // Set up filters (post_type, etc.)
        this.setupFilters();

        // Initialize reset link visibility
        this.updateResetLinkVisibility();

        // Track event link clicks for scroll-to-anchor on return
        this.setupEventLinkTracking();

        // Check if we need to scroll to this block after navigation
        this.checkAndScrollToBlock();
    }

    checkAndScrollToBlock() {
        const scrollToBlock = sessionStorage.getItem('scrollToBlock');

        if (scrollToBlock && scrollToBlock === this.element.id) {
            // Small delay to ensure page is fully loaded
            setTimeout(() => {
                this.scrollToBlock();
                sessionStorage.removeItem('scrollToBlock');
            }, 100);
        }
    }

    scrollToBlock() {
        const block = this.element;

        // Get sticky header height from spacer
        let headerHeight = 0;
        const siteHeader = document.querySelector('.site-header');
        if (siteHeader) {
            headerHeight = siteHeader.offsetHeight;
        }

        const blockRect = block.getBoundingClientRect();
        const blockPosition = blockRect.top + window.pageYOffset;
        const scrollPosition = blockPosition - headerHeight;

        window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
        });
    }

    setupFilters() {
        const selects = this.element.querySelectorAll('select');

        selects.forEach((select) => {
            this.postType = select.value; // Store initial value (could be "event,partner-event")

            select.addEventListener('change', () => {
                this.postType = select.value;

                // Re-fetch events with current date and new post type
                const currentDate = this.getCurrentActiveDate();
                this.fetchAndDisplayEvents(currentDate || { year: null, month: null, day: null });
            });
        });
    }

    getCurrentActiveDate() {
        // Find current active element and extract date from URL
        const { calendarCell, calendarStripItemTitle } = this.constructor.selectors;
        const { currentClass } = this.constructor.classes;
        const activeElement = this.element.querySelector(`${calendarCell}.${currentClass}, ${calendarStripItemTitle}.${currentClass}`);

        if (!activeElement) {
            return null;
        }

        const href = activeElement.getAttribute('href');
        if (!href) {
            return null;
        }

        // Extract date from URL like /events/2025/11/24/
        const match = href.match(/\/(\d{4})(?:\/(\d{2}))?(?:\/(\d{2}))?/u);
        if (!match) {
            return null;
        }

        const [, year, month, day] = match;
        return { year, month: month || null, day: day || null };
    }

    loadEvents(event) {
        event.preventDefault();

        const target = event.currentTarget;
        const { currentClass, noEventsClass } = this.constructor.classes;

        // Don't allow clicking on non-active days
        if (target.classList.contains(noEventsClass)) {
            return;
        }

        // Check if clicking on already-current element
        const isCurrentlyActive = target.classList.contains(currentClass);

        if (isCurrentlyActive) {
            // Unselect: remove active state and fetch all events
            this.removeActiveState();
            this.fetchAndDisplayEvents({ year: null, month: null, day: null });
        } else {
            // Extract date from href
            const href = target.getAttribute('href');
            const match = href.match(/\/(\d{4})(?:\/(\d{2}))?(?:\/(\d{2}))?/u);
            if (!match) {
                return;
            }

            const [, year, month, day] = match;

            // Update active state
            this.updateActiveState(target);

            // Fetch and display events
            this.fetchAndDisplayEvents({ year, month: month || null, day: day || null });
        }
    }

    updateActiveState(clickedElement) {
        const { currentClass } = this.constructor.classes;
        const { calendarStripItem } = this.constructor.selectors;

        // Remove current class from all elements
        this.element.querySelectorAll(`.${currentClass}`).forEach(el => {
            el.classList.remove(currentClass);
        });

        // Add current class to clicked element
        clickedElement.classList.add(currentClass);

        // Also add to parent item
        const parentItem = clickedElement.closest(calendarStripItem);
        if (parentItem) {
            parentItem.classList.add(currentClass);
        }

        // Update reset link visibility
        this.updateResetLinkVisibility();
    }

    removeActiveState() {
        const { currentClass } = this.constructor.classes;

        // Remove current class from all elements
        this.element.querySelectorAll(`.${currentClass}`).forEach(el => {
            el.classList.remove(currentClass);
        });

        // Update reset link visibility
        this.updateResetLinkVisibility();
    }

    resetSelection(event) {
        event.preventDefault();

        // Remove all active states
        this.removeActiveState();

        // Fetch all events (no date filter)
        this.fetchAndDisplayEvents({ year: null, month: null, day: null });
    }

    updateResetLinkVisibility() {
        const { resetButtons, linkBelow } = this.constructor.selectors;
        const { currentClass } = this.constructor.classes;

        // Check if any calendar cell or title is currently selected
        const hasSelection = this.element.querySelector(`.${currentClass}`) !== null;

        const linkBelowSlider = this.element.querySelector(linkBelow);
        if (linkBelowSlider) {
            linkBelowSlider.style.display = hasSelection ? 'none' : '';
        }

        // Show/hide all reset/back buttons based on selection state
        const buttons = this.element.querySelectorAll(resetButtons);
        buttons.forEach(button => {
            button.style.display = hasSelection ? '' : 'none';
        });
    }

    setupEventLinkTracking() {
        // Track clicks on event links within this calendar block
        this.element.addEventListener('click', (event) => {
            // Check for any event link (tour-event, partner-event, event, private-event)
            const link = event.target.closest('a[href*="/tour-event/"], a[href*="/partner-event/"], a[href*="/event/"], a[href*="/private-event/"]');

            if (link && this.element.id) {
                // Store this block's ID for scroll-to-anchor on return
                sessionStorage.setItem('scrollToBlock', this.element.id);
            }
        });
    }

    fetchAndDisplayEvents(dateObj) {
        // Build event_date string if date is provided
        const filters = { ...this.filters };

        let eventDate = '';

        if (dateObj && dateObj.year) {
            eventDate = dateObj.year;
            if (dateObj.month) {
                eventDate += dateObj.month;
            }
            if (dateObj.day) {
                eventDate += dateObj.day;
            }
        }

        // Show loading state
        this.emptyEvents();
        this.showLoading();

        // Fetch events with current post type
        this.fetchEvents(this.postType, 0, 20, filters, eventDate).then(response => {
            if (response && response.data) {
                this.insertEvents(response.data);
                SliderController.refreshSliders();
            } else {
                this.showNoEvents();
            }
        }).catch(() => {
            this.showNoEvents();
        });
    }

    async fetchEvents(type, from, limit, filters, event_date) {
        const data = {
            rest_language: this.currentLanguageCode,
            type: type,
            from: from,
            limit: limit,
            filters: JSON.stringify(filters),
            [this.eventDateParamValue]: event_date,
        };

        const params = Object.keys(data).map(function (k) {
            return `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`;
        }).join('&');

        const url = `/wp-json/api/fetch-posts/?${params}`;

        let response = null;
        try {
            response = await Axios.get(url);
            response = response.data.data;
        } catch (error) {
            response = error.response?.data?.data || null;
        }

        return response;
    }

    showLoading() {
        if (this.hasContainerTarget) {
            const { loadingClass, noEventsStateClass } = this.constructor.classes;
            this.containerTarget.classList.remove(noEventsStateClass);
            this.containerTarget.classList.add(loadingClass);
        }
    }

    emptyEvents() {
        const { splideList } = this.constructor.selectors;
        const list = this.element.querySelector(splideList);
        list.innerHTML = '';
    }

    insertEvents(html) {
        if (this.hasContainerTarget) {
            const { loadingClass, noEventsStateClass } = this.constructor.classes;
            this.containerTarget.classList.remove(loadingClass);
            this.containerTarget.classList.remove(noEventsStateClass);
        }
        const { splideList } = this.constructor.selectors;
        const list = this.element.querySelector(splideList);
        list.innerHTML = html;
    }

    showNoEvents() {
        if (!this.hasContainerTarget) {
            return;
        }

        const { loadingClass, noEventsStateClass } = this.constructor.classes;
        this.containerTarget.classList.remove(loadingClass);
        this.containerTarget.classList.add(noEventsStateClass);
        this.emptyEvents();
    }
}
